import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { clsx } from 'clsx'
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  SubmitHandler,
  useForm,
} from 'react-hook-form'
import toast from 'react-hot-toast'

import FieldError from '@/components/field-error'
import Avatar from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  isValidUrl,
  rejectUndefinedAndNull,
} from '@/lib/utils'
import { useUploadMutation } from '@/redux/file'
import {
  useReadMineUserQuery,
  useUpdateMineUserMutation,
} from '@/redux/user'

interface FormValues {
  nickname?: string
  introduction?: string
  socialLink1?: string
  socialLink2?: string
  socialLink3?: string
}

const maxIntroductionLength = 200
const maxNicknameLength = 42

const socialLinkOption = {
  validate: (value: string | undefined) => {
    if (!value) return true
    return isValidUrl(value) || 'Please enter a valid URL'
  },
}

export default function Basic() {
  const { data: mineUser } = useReadMineUserQuery()
  const [newAvatar, setNewAvatar] = useState<string>()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const handleUpload = useCallback(() => {
    fileInputRef.current?.click()
  }, [])
  const [upload, { isLoading: isUploading }] = useUploadMutation()
  const [update] = useUpdateMineUserMutation()

  const handleFileChange = useCallback(async ({ target: { files } }: ChangeEvent<HTMLInputElement>) => {
    const file = files?.[0]
    if (!file) return
    const { data: accessUrl } = await upload(file)
    if (!accessUrl) return
    setNewAvatar(accessUrl)
    toast.success('Upload avatar success')
  }, [upload])

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>()
  const onSubmit: SubmitHandler<FormValues> = async ({
    nickname,
    introduction,
    socialLink1,
    socialLink2,
    socialLink3,
  }) => {
    await update({
      avatar: newAvatar,
      nickname,
      introduction,
      socialLinks: [socialLink1, socialLink2, socialLink3].filter(rejectUndefinedAndNull),
    }).unwrap()
    toast.success('Profile updated!')
  }
  const introduction = watch('introduction')
  const introductionLength = introduction?.length || 0
  const nickname = watch('nickname')
  const nicknameLength = nickname?.length || 0
  useEffect(() => {
    if (mineUser) {
      const { socialLinks } = mineUser
      reset({
        nickname: mineUser.nickname,
        introduction: mineUser.introduction,
        socialLink1: socialLinks[0],
        socialLink2: socialLinks[1],
        socialLink3: socialLinks[2],
      })
    }
  }, [mineUser, reset])

  if (!mineUser) return null

  return (
    <form
      autoComplete="off"
      className="flex flex-col gap-16"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex gap-6 items-center">
        <Avatar size="lg" src={newAvatar || mineUser.avatar} />
        <VisuallyHidden>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
          />
        </VisuallyHidden>
        <Button isLoading={isUploading} size="lg" type="button" onClick={handleUpload} disabled={isUploading}>
          Upload Avatar
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-10">
        <div>
          <Label required className="mb-3" htmlFor="nickname">Nickname</Label>
          <div className="flex gap-4 items-center">
            <div className="relative w-[450px] shrink-0">
              <Input
                {...register(
                  'nickname',
                  {
                    required: 'Nickname cannot be empty',
                    pattern: {
                      value: /^[\u4e00-\u9fa5a-zA-Z0-9_]*$/,
                      message: 'Nickname cannot contain special character',
                    },
                    maxLength: {
                      value: maxNicknameLength,
                      message: `Please keep your nickname within ${maxNicknameLength} characters`,
                    },
                  },
                )}
                id="nickname"
                aria-invalid={!!errors.nickname}
                placeholder="Please input your nickname"
                onClear={nicknameLength ? () => reset({ nickname: '' }) : undefined}
              />
            </div>
            <div
              className={clsx(
                'whitespace-nowrap text-xs',
                errors.nickname || nicknameLength > maxNicknameLength ? 'text-destructive' : 'text-secondary-foreground',
              )}
            >
              {nicknameLength} / {maxNicknameLength}
            </div>
          </div>
          <FieldError error={errors.nickname} />
        </div>
      </div>
      <div>
        <div className="flex justify-between mb-3">
          <Label htmlFor="introduction">Brief self-introduction</Label>
          <div className="flex gap-4">
            <div className="text-xs text-destructive">{errors.introduction?.message}</div>
            <div className={clsx(
              'text-xs',
              errors.introduction || introductionLength > maxIntroductionLength ? 'text-destructive' : 'text-secondary-foreground',
            )}>
              {introductionLength} / {maxIntroductionLength}
            </div>
          </div>
        </div>
        <Textarea
          {...register(
            'introduction',
            {
              maxLength: {
                value: maxIntroductionLength,
                message: `Bio must be under ${maxIntroductionLength} characters`,
              },
            },
          )}
          aria-invalid={!!errors.introduction}
          id="introduction"
          placeholder="Tell us a bit about yourself..."
        />
      </div>
      <div className="flex flex-col gap-6">
        <div className="text-lg">Social Link</div>
        <div className="grid grid-cols-2 gap-10">
          <div>
            <Label className="mb-3" htmlFor="socialLink1">Social Link 1</Label>
            <Input
              {...register('socialLink1', socialLinkOption)}
              id="socialLink1"
              aria-invalid={!!errors.socialLink1}
              placeholder="Paste your social media link"
            />
            <FieldError error={errors.socialLink1} />
          </div>
          <div>
            <Label className="mb-3" htmlFor="socialLink2">Social Link 2</Label>
            <Input
              {...register('socialLink2', socialLinkOption)}
              id="socialLink2"
              aria-invalid={!!errors.socialLink2}
              placeholder="Paste your social media link"
            />
            <FieldError error={errors.socialLink2} />
          </div>
          <div>
            <Label className="mb-3" htmlFor="socialLink3">Social Link 3</Label>
            <Input
              {...register('socialLink3', socialLinkOption)}
              id="socialLink3"
              aria-invalid={!!errors.socialLink3}
              placeholder="Paste your social media link"
            />
            <FieldError error={errors.socialLink3} />
          </div>
        </div>
      </div>
      <Button
        variant="primary"
        isLoading={isSubmitting}
        type="submit"
        className="w-40"
        size="xl"
        disabled={isSubmitting}
      >
        Save
      </Button>
    </form>
  )
}
